
import { Injectable } from '@angular/core';

import { HttpClient, HttpResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { DashboardConfigManager } from '../../services/dashboard.config.service';
import { AuthService } from '../../services/auth.service';
import { WsiUtilityService } from '../../services/wsi.helper.service';
import { LogService } from "../../logger/loggerProvider/loggerhelper.service";
import { Observable } from "rxjs";
import { Subscription } from "rxjs";
import { Subject } from "rxjs";
import { throwError } from 'rxjs';
import { HubConnection, HubProxy, SignalRService, SubscriptionWsi, SubscriptionDeleteWsi, Link } from '../../signalr/index';
import { ValueSubscriptionProxy } from './wsi.objectvalue.proxy';
import { Value, ValueDetails } from '../../interfaces/wsi.value.data';
import { catchError, map } from 'rxjs/operators';
const valuesSubscriptionUrl: string = "/api/sr/valuessubscriptions/";
export const defaultClientId: string = "defaultClient";

@Injectable()
export class WSIValueService {

    config: any;
    private hubProxy: HubProxy<any> = undefined;
    private subscriptionProxies: Map<string, ValueSubscriptionProxy> = new Map<string, ValueSubscriptionProxy>();
    private connectionId: any;

    constructor(private authService: AuthService,
        private wsiHelper: WsiUtilityService,
        private http: HttpClient,
        private dashboardConfig: DashboardConfigManager, private loggerHelper: LogService, private _signalRService: SignalRService) {
        //Setup SignalR Hub and Proxy
        this.createConnection();
        //this.createDefaultClientProxy();
        this.hubProxy.hubConnection.connected.subscribe(value => this.onSignalRConnection(value), error => this.onSignalRConnectionError(error));
    }

    private createConnection(): void {
        let baseUrl = this.dashboardConfig.getBaseUrl();
        let hubConnection: HubConnection = this._signalRService.createHubConnection(baseUrl + "/signalr", WSIValueService.name);
        this.hubProxy = new HubProxy<any>(hubConnection, "valuesHub", "notifyValues", WSIValueService.name);
    }

    public createClientId(clientName: string): string {
        let proxy: ValueSubscriptionProxy = new ValueSubscriptionProxy(this.hubProxy.events, clientName,this.loggerHelper);
        this.subscriptionProxies.set(proxy.clientId, proxy);
        return proxy.clientId;
    }

    public disposeClientId(clientId: string): void {
        let proxy: ValueSubscriptionProxy = this.subscriptionProxies.get(clientId);
        if (proxy != undefined) {
            proxy.dispose();
            this.subscriptionProxies.delete(clientId);
        }
        else {
            //TODO LOG
        }
    }

    /**
   * Creates subscription for given set of ObjectIds or PropertyIds.
   *
   * @param {string[]} [objectOrPropertyIds] Array of ObjectIds or PropertyIds for creating subscriptions.
   * @param {boolean} [details] Details flag for Values.
   * @param {string} [clientId] Client Id for subscription.
   * @returns {Observable<SubscriptionWsi[]>}
   *
   * @memberOf ValueSubscriptionService
   */
    public subscribeValues(objectOrPropertyIds: string[], details: boolean = false,
        clientId: string): Observable<SubscriptionWsi[]> {
        let subscriptionProxy: ValueSubscriptionProxy = this.subscriptionProxies.get(clientId);
        if ((objectOrPropertyIds == null) || (objectOrPropertyIds.length === 0) || (subscriptionProxy == undefined)) {
            this.loggerHelper.logWarning(WSIValueService.name, " subscribeValues() called with invalid arguments: clientId=" + clientId);
            return throwError(new Error("Invalid arguments!"));
        }
        this.loggerHelper.logInfo(WSIValueService.name, "subscribeValues() called;\nclientId=" + clientId + "; number of objectOrPropertyIds:" + objectOrPropertyIds.length);
        //let headers: HttpHeaders = this.wsiHelper.httpPostDefaultHeader(this.authService.getToken());
        let token: any = this.authService.getToken();// window.sessionStorage.getItem("_token");
        let headers: HttpHeaders = new HttpHeaders({ "Accept": "application/json", "Content-Type":"application/json; charset=utf-8","Access-Control-Allow-Origin": "*", "Authorization": "Bearer " + token });
        let body: any = JSON.stringify(objectOrPropertyIds);
        let params: HttpParams = new HttpParams();
        params.set("detailsRequired", String(details));

        let httpPostProxy: Subject<SubscriptionWsi[]> = new Subject<SubscriptionWsi[]>();
        if (this.hubProxy.hubConnection.isConnected === false) {
            this.loggerHelper.logInfo(WSIValueService.name, "subscribeValues(): signalr connection not established; need to wait (and postpone http calls) until established in order to get connection id.");
            let connectedSubscription: Subscription = this.hubProxy.hubConnection.connected.subscribe(started => {
                if (started === true) {
                    this.loggerHelper.logInfo(WSIValueService.name, "subscribeValues(): connected event triggered: connection is now established.");
                    // connection ID is available now, we can setup the 'post observable' now and not earlier
                    if (connectedSubscription !== undefined) {
                        connectedSubscription.unsubscribe();
                    }
                    else {
                        this.loggerHelper.logError(WSIValueService.name, "subscribeValues(); Implementation error, we should not reach this!");
                    }
                    let url: string = this.dashboardConfig.getBaseUrl() + valuesSubscriptionUrl + this.hubProxy.connectionId;
                    let httpPost: Observable<any[]> = this.http.post(url, body, { headers: headers,  params })
                        .pipe(map(response => this.wsiHelper.extractData(response as any, WSIValueService.name, "subscribeValues()"))
                        ,catchError(response => this.wsiHelper.handleError(response, WSIValueService.name, "subscribeValues()")));

                    httpPost.subscribe(value => this.onSubscribeValues(value, objectOrPropertyIds, subscriptionProxy, httpPostProxy),
                        error => this.onSubscribeValuesError(error, httpPostProxy));
                }
            });
            this.hubProxy.hubConnection.startHubConnection();
        }
        else {
            let url: string = this.dashboardConfig.getBaseUrl() + valuesSubscriptionUrl + this.hubProxy.connectionId;
            let httpPost: Observable<any[]> = this.http.post(url, body, { headers: headers,  params })
                .pipe(map(response => this.wsiHelper.extractData(response as any, WSIValueService.name, "subscribeValues()"))
                ,catchError(response => this.wsiHelper.handleError(response, WSIValueService.name, "subscribeValues()")));

            httpPost.subscribe(value => this.onSubscribeValues(value, objectOrPropertyIds, subscriptionProxy, httpPostProxy),
                error => this.onSubscribeValuesError(error, httpPostProxy));
        }
        return httpPostProxy.asObservable();
    }

    /**
   * Event for the value notifications.
   *
   * @param {string} [clientId=defaultClientId] used to listen to the value notifications of the specified client.
   * @returns {Observable<ValueDetails[]>}
   *
   * @memberOf ValueSubscriptionService
   */
    public valueChangeNotification(clientId: string = defaultClientId): Observable<ValueDetails[]> {
        let subscriptionProxy: ValueSubscriptionProxy = this.subscriptionProxies.get(clientId);
        if (subscriptionProxy == undefined) {
            return throwError(new Error("Invalid client id!"));
        }
        return subscriptionProxy.valueNotification;
    }

    private onSubscribeValues(values: SubscriptionWsi[], requestedIds: string[],
        clientProxy: ValueSubscriptionProxy, httpPostProxy: Subject<SubscriptionWsi[]>): void {
        values.forEach(subWsi => {
            if (subWsi.ErrorCode === 0) {
                clientProxy.addSubscription(subWsi);
            }
            else {
                this.loggerHelper.logWarning(WSIValueService.name, "onSubscribeValues(); Error occured in Subscription!");
            }
        });
        httpPostProxy.next(values);
        httpPostProxy.complete();
    }

    private onSubscribeValuesError(error: any, httpPostProxy: Subject<SubscriptionWsi[]>): void {
        this.loggerHelper.logWarning(WSIValueService.name, "onSubscribeValuesError(): Error occured during Value subscription, Error : " + error);
        httpPostProxy.error(error);
    }

    /**
  * Unsubscribes objectOrPropertyIds (associated with the subscription keys). See WSI API for details
  *
  * @param {number[]} keys
  * @param {string} [clientId=defaultClientId]
  * @returns {Observable<SubscriptionDeleteWsi[]>}
  *
  * @memberOf ValueSubscriptionService
  */
    public unSubscribeValues(keys: number[], clientId: string = defaultClientId): Observable<SubscriptionDeleteWsi[]> {
        let subscriptionProxy: ValueSubscriptionProxy = this.subscriptionProxies.get(clientId);
        if ((keys == null) || (keys.length === 0) || (subscriptionProxy == undefined)) {
            return throwError(new Error("Invalid arguments!"));
        }
        let index: number = keys.findIndex(item => {
            return (item == undefined) ? true : false;
        });
        if (index !== -1) {
            keys = keys.filter(item => {
                return (item != undefined) ? true : false;
            });
        }
        if (keys.length === 0) {
            return throwError(new Error("Invalid arguments!"));
        }

        //let headers: HttpHeaders = this.wsiHelper.httpPostDefaultHeader(this.authService.getToken());
        let token: any = this.authService.getToken();// window.sessionStorage.getItem("_token");
        let headers: HttpHeaders = new HttpHeaders({ "Accept": "application/json", "Content-Type":"application/json; charset=utf-8","Access-Control-Allow-Origin": "*", "Authorization": "Bearer " + token });
        let keysJson: any = JSON.stringify(keys);
        let params: HttpParams = new HttpParams();
        params.set("subscriptionKey", keysJson);

        let httpPostProxy: Subject<SubscriptionDeleteWsi[]> = new Subject<SubscriptionDeleteWsi[]>();
        if (this.hubProxy.hubConnection.isConnected === false) {

            let connectedSubscription: Subscription = this.hubProxy.hubConnection.connected.subscribe(started => {
                if (started === true) {
                    // connection ID is available now, we can setup the 'post observable' now and not earlier
                    // => due to this we cannot use rxjs merge stream functionality such as 'concat'!!
                    if (connectedSubscription !== undefined) {
                        connectedSubscription.unsubscribe();
                    }

                    let url: string = this.dashboardConfig.getBaseUrl() + valuesSubscriptionUrl + this.hubProxy.connectionId;
                    let httpPost: Observable<SubscriptionDeleteWsi[]> = this.http.delete(url, { headers: headers, params })
                        .pipe(map(response => this.wsiHelper.extractData(response as any, WSIValueService.name, "unSubscribeValues()"))
                        ,catchError(response => this.wsiHelper.handleError(response, WSIValueService.name, "unSubscribeValues()")));
                    httpPost.subscribe(value => this.onUnsubscribeValues(value, keys, subscriptionProxy, httpPostProxy),
                        error => this.onUnsubscribeValuesError(error, httpPostProxy));
                }
            });
            this.hubProxy.hubConnection.startHubConnection();
        }
        else {
            let url: string = this.dashboardConfig.getBaseUrl() + valuesSubscriptionUrl + this.hubProxy.connectionId;
            let httpPost: Observable<SubscriptionDeleteWsi[]> = this.http.delete(url, { headers: headers,params })
                .pipe(map(response => this.wsiHelper.extractData(response as any, WSIValueService.name, "unSubscribeValues()"))
                ,catchError(response => this.wsiHelper.handleError(response, WSIValueService.name, "unSubscribeValues()")));
            httpPost.subscribe(value => this.onUnsubscribeValues(value, keys, subscriptionProxy, httpPostProxy),
                error => this.onUnsubscribeValuesError(error, httpPostProxy));
        }
        return httpPostProxy.asObservable();
    }

    private onUnsubscribeValues(values: SubscriptionDeleteWsi[], requestedKeys: number[],
        clientProxy: ValueSubscriptionProxy, httpPostProxy: Subject<SubscriptionDeleteWsi[]>): void {
        values.forEach(subDelWsi => {
            if (subDelWsi.ErrorCode === 0) {
                clientProxy.removeSubscription(subDelWsi);
            }
            else {
                this.loggerHelper.logWarning(WSIValueService.name, "onUnsubscribeValues(); Error occured in unsubscription!");
            }
        });
        httpPostProxy.next(values);
        httpPostProxy.complete();
    }

    private onUnsubscribeValuesError(error: any, httpPostProxy: Subject<SubscriptionDeleteWsi[]>): void {
        this.loggerHelper.logWarning(WSIValueService.name, "onUnsubscribeValuesError(); Error occured in unsubscription Error :" + error);

        httpPostProxy.error(error);
    }

    private onSignalRConnectionError(error: any): void {
        this.loggerHelper.logWarning(WSIValueService.name, "onSignalRConnectionError(); Error occured in SignalR Connection, Error :" + error);
    }

    private onSignalRConnection(value: boolean): void {
        this.loggerHelper.logInfo(WSIValueService.name, "onSignalRConnection(); SignalR Connection Value :" + value);
    }

    private createDefaultClientProxy(): void {
        let proxy: ValueSubscriptionProxy = new ValueSubscriptionProxy(this.hubProxy.events, undefined,this.loggerHelper);
        this.subscriptionProxies.set(proxy.clientId, proxy);
    }
}



